<script>
    import _ from 'lodash'

    const removeElement = (el) => {
        typeof el.remove !== 'undefined' ? el.remove() : el.parentNode.removeChild(el)
    }

    export default {
        name: 'VGridformDialog',
        props: {
            /**
             * @typedef options
             * @property {String} options.name
             * @property {String} options.text
             * @property {String} options.description
             * @property {String} options.message
             * @property {String} options.title
             * @property {String} options.subtitle
             * @property {String} options.icon
             * @property {String} options.iconTitle
             * @property {String|Number} options.width
             * @property {Boolean} options.input
             * @property {Boolean} options.textarea
             * @property {Boolean} options.gridform
             * @property {Boolean} options.discard
             * @property {Boolean} options.fullscreen
             * @property {Array} options.actions
             */
            options: {
                type: Object,
                default: () => ({})
            }
        },
        data() {
            return {
                open: true,
                inputText: '',
                checklist: null,
                keyHandler: null
            }
        },

        computed: {
            submitText() {
                return this.options.submit || this.$vuetify.lang.t('$vuetify.actions.ok')
            },
            cancelText() {
                return this.options.cancel || this.$vuetify.lang.t('$vuetify.actions.cancel')
            },
            saveText() {
                return this.options.saveText || this.$vuetify.lang.t('$vuetify.actions.save')
            },
            discardText() {
                return this.options.discardText || this.$vuetify.lang.t('$vuetify.actions.discard')
            },
            dialogWidth() {
                if (this.options.width) return this.options.width

                const large = this.options.large
                const bp = _.get(this.$vuetify, 'breakpoint.name')
                let width
                switch(bp) {
                    case 'xs': width = '98%'; break
                    case 'sm': width = large ? '98%' : '75%'; break
                    case 'md': width = large ? '75%' : '50%'; break
                    case 'lg': width = large ? '60%' : '640px'; break
                    case 'xl': width = large ? '50%' : '640px'; break
                    default: width = '50%'; break
                }
                return width
            },
            disableSubmit() {
                const required = _.get(this.options, 'input.required')
                return required ? !this.inputText : false
            }
        },
        watch: {
            open() {
                // open changed on backdrop click, if dialog is not permanent
                if (!this.open) this.cancel()
            }
        },

        beforeMount() {
            const $app = document.querySelector('#app') || document.body || document.getElementsByTagName('body')[0]
            if ($app) $app.appendChild(this.$el)
            this.checklist = this.options.checklist
        },
        mounted() {
            this.$options.onMounted()
            const ref = !this.options.input && !this.options.textarea ? 'submit' : 'input'
            if (_.isArray(this.$refs[ref])) this.$autofocus(this.$refs[ref][0])
            else this.$autofocus({ ref })
            if (this.checklist) {
                this.keyHandler = event => {
                    if (event.code === 'KeyA' && event.ctrlKey) {
                        event.preventDefault()
                        // toggle checkbox states
                        this.checklist = this.checklist.map(cl => {
                            cl.inputValue = !cl.inputValue
                            return cl
                        })
                    }
                }
                document.addEventListener('keydown', this.keyHandler, false)
            }
        },
        beforeDestroy() {
            document.removeEventListener('keydown', this.keyHandler, false)
            this.keyHandler = null
        },

        methods: {
            submit(discard) {
                let args = null
                if (this.options.input || this.options.textarea) args = { inputText: this.inputText }
                if (discard === true) args = { discard: true } // discard could be an event
                if (this.checklist) {
                    args = {}
                    _.each(this.$refs, (ref, refName) => {
                        if (/^checklist/.test(refName)) {
                            const vc = _.first(ref)
                            if (vc && vc.$attrs && vc.$refs) args[vc.$attrs.name] = vc.$refs.input ? vc.$refs.input.checked : vc.value
                        }
                    })
                }
                this.close()
                this.$emit('submit', args)
            },
            cancel() {
                this.close()
                this.$emit('cancel')
            },
            onAction(action) {
                this.close()
                this.$emit('submit', {action})
            },
            close() {
                setTimeout(() => {
                    this.$options.onClose()
                    this.$destroy()
                    removeElement(this.$el)
                }, 400) // wait for close animation
            }
        }
    }
</script>

<template>
    <VDialog v-model="open" class="vgf-dialog" :max-width="dialogWidth" :fullscreen="options.fullscreen" scrollable>
        <template v-if="options.gridform">
            <VGridform :name="options.name" v-bind="options.gridform"/>
        </template>
        <VCard v-else class="vgf-dialog__card" elevation="24">
            <VCardTitle>
                <VIcon v-if="options.iconTitle" left v-text="options.iconTitle"></VIcon>
                <span class="text-h5">{{ options.message || options.title }}</span>
            </VCardTitle>
            <VCardSubtitle v-if="options.description || options.subtitle" v-text="options.description || options.subtitle"></VCardSubtitle>
            <VCardText>
                <div class="d-flex align-center">
                    <VIcon v-if="options.icon" large left v-text="options.icon"></VIcon>
                    <span v-if="options.text" class="text-h7" v-text="options.text"></span>
                </div>
                <VTextField v-if="options.input" ref="input" v-model="inputText" v-bind="options.input"/>
                <VTextarea v-if="options.textarea" ref="input" v-model="inputText" v-bind="options.textarea"/>
                <template v-if="checklist">
                    <template v-for="(checks, cidx) in checklist">
                        <VDivider v-if="checks.divider" :key="cidx" class="my-4"/>
                        <VCheckbox v-else :key="cidx" :ref="`checklist${cidx}`" v-bind="checks"
                            :true-value="true"
                            :false-value="false"
                            dense
                        />
                    </template>
                </template>
            </VCardText>
            <VCardActions>
                <VSpacer/>
                <template v-if="options.actions">
                    <VBtn v-for="item in options.actions" :key="item.action"
                        :ref="item.ref"
                        :class="{ accent: item.accent }"
                        :type="item.type"
                        text
                        :outlined="item.outlined"
                        @click.left="onAction(item.action)"
                        v-text="item.text"/>
                </template>
                <template v-else-if="options.discard">
                    <VBtn color="accent" @click.left="submit" v-text="saveText"/>
                    <VBtn outlined @click.left="submit(true)" v-text="discardText"/>
                    <VBtn ref="submit" type="submit" outlined @click.left="cancel" v-text="cancelText"/>
                </template>
                <template v-else>
                    <VBtn outlined @click.left="cancel" v-text="cancelText"/>
                    <VBtn ref="submit"
                        type="submit"
                        color="accent"
                        :disabled="disableSubmit"
                        @click.left="submit"
                        v-text="submitText"/>
                </template>
            </VCardActions>
        </VCard>
    </VDialog>
</template>

<style>
    .vgf-dialog__card .v-card__text {
        width: 90%;
    }
    .vgf-dialog__card .v-card__text .v-icon.v-icon--left {
        margin-right: 24px;
    }
    .vgf-dialog__card.theme--dark {
        background-color: #121212;
    }
</style>
